import { ICitation } from '@vivli/shared/features/citations/infrastructure/interface';
import { useMemo } from 'react';

///This hook is used to format the citation for display currently in MLA format, e.g.
/// Alonso, Alvaro, and Julio A. Camargo. "Toxicity of Nitrite to Three Species of Freshwater Invertebrates."
/// Environmental Toxicology, vol. 21, no. 1, 3 Feb. 2006, pp. 90-94. Wiley Online Library, doi: 10.1002/tox.20155.
///NOTE journal title should be rendered in italics,.
export function useCitation(citation: ICitation) {
    /// <summary>return the authors in the format: Last, F. M. & Last, F. M.</summary>
    const formattedAuthors = (useEtAl: boolean) => {
        let numAuthors = citation?.authors?.length;
        if (numAuthors === 0 || !citation || citation === undefined) {
            return '-';
        }

        let finalSeparator = ' and ';
        let finalSeparatorIndex = 0;
        switch (numAuthors) {
            case 1:
                break;
            default:
                finalSeparatorIndex = numAuthors - 2;
                break;
        }

        let authorIndex = 0;
        let authorString = '';

        //some manual citations have an extra '' author, so we need to check for that
        const goodAuthors = citation.authors.filter((author) => author.givenName !== '' && author.familyName !== '');

        numAuthors = goodAuthors.length;

        goodAuthors.map((author) => {
            //Note: I used + instead of interpolation to make it easier to see the strings
            //get first initial of given name
            //const givenInitial = author.givenName.slice(0, 1) + '.';

            //get the final separator and string
            const separator = authorIndex === finalSeparatorIndex;

            //format the author
            const separatorString = separator ? finalSeparator : ', ';
            const formatted = author.familyName + ', ' + author.givenName + separatorString;

            //add to the author string
            authorString = authorString + formatted;
            authorIndex++;
        });
        const trimAmount = numAuthors === 1 ? -4 : -2;

        const formatted = authorString.slice(0, trimAmount);
        return formatted;
    };

    /// <summary>return the rest of the citation in the format: Journal Volume, Month Year Number Pages Doi
    /// for use on the Public Disclosures grids, where things are very compact
    // </summary>
    const remainderFormatted = (withDoi: boolean = true) => {
        if (!citation || citation === undefined) {
            return '-';
        }
        let remainderString;
        const urlLabel = citation.doi ? 'doi: ' : 'url: ';

        if (citation.doi) {
            remainderString = ` vol. ${citation.volume},
            no. ${citation.number}, ${citation.monthPublished}. ${citation.yearPublished},  pp. ${citation.pages}, `;
        } else {
            //smaller set of things to show for non-doi citations
            const locationString = citation.pages ? ` ${citation.pages},` : '';
            remainderString = `${locationString} ${citation.yearPublished}, `;
        }

        if (withDoi) {
            remainderString += `
            ${urlLabel}${citation.url}`;
        }

        return remainderString;
    };

    /// <summary>return the journal in the format: Journal Volume, Month Year Number Pages
    /// For use with published requests. noTitle option is to allow
    ///separate styling for the journal title
    /// </summary>
    const formattedJournal = (noTitle: boolean) => {
        if (!citation || citation === undefined) {
            return '-';
        }
        let journal = '';
        if (citation?.doi) {
            journal = noTitle
                ? `vol. ${citation.volume}, no. ${citation.number}, ${citation.monthPublished}. ${citation.yearPublished},  pp. ${citation.pages}`
                : `${citation.journal} vol. ${citation.volume}, no. ${citation.number}, ${citation.monthPublished}. ${citation.yearPublished},  pp. ${citation.pages}`;
        } else {
            //smaller set to show for non-doi citations
            const locationString = citation?.pages ? ` ${citation?.pages},` : '';
            journal = `${locationString} ${citation?.yearPublished}`;
        }
        return journal;
    };

    const formattedDoi = () => {
        if (!citation || citation === undefined) {
            return '-';
        }
        if (citation.doi === null || citation.doi === undefined || citation.doi === '') {
            //this is for a manual citation (no doi, but needs a url)
            return citation.url ?? '-';
        }

        if (citation.doi.slice(0, 3) === '10.') {
            return `https://doi.org/${citation.doi}`;
        }

        return `${citation.doi}`;
    };

    const noDoiCitation = useMemo(() => {
        return formattedDoi() === '-';
    }, [citation]);

    const formattedJournalText = useMemo(() => {
        return citation?.journal ? `${citation?.journal},` : '-';
    }, [citation]);

    const formattedTitleText = useMemo(() => {
        return citation ? `"${citation?.title}".` : '-';
    }, [citation]);

    return {
        formattedJournal,
        formattedAuthors,
        remainderFormatted,
        formattedDoi,
        noDoiCitation,
        formattedJournalText,
        formattedTitleText,
    };
}
